import React from 'react'
// libs
import { useLazyQuery } from '@apollo/client/react'
import { debounce } from 'lodash'
// components
import { ReduxSelect } from 'components/FormElements/ReduxForm'
// graphql
import { INJURY_MASTER_RECORDS_QUERY } from 'graphql/injuryMaster/queries'
// types
import { IOption } from 'components/FormElements/Select'
import { InjuryMasterListType } from 'types/graphql'
// utils
import { required } from 'utils/validators'
// views
import { Hint } from './views'

interface Props {
  name: string
  label: string
  listType: InjuryMasterListType
  isMultiple?: boolean
  // false for a row in a per-option record map — an option left unmapped is allowed, not every
  // Skefto option needs an Injury Master record.
  isRequired?: boolean
}

const LOADING_MORE_HINT = 'Loading more...'

// A searchable, scrollable dropdown over one of Injury Master's own record lists (mechanism, body
// part, policy…), server-side filtered (`name__icontains`) once the person types. Scrolling to the
// bottom loads the next page of whatever is currently shown — the full list or a search's results.
const RecordPicker: React.FC<Props> = ({ name, label, listType, isMultiple, isRequired = true }) => {
  const [options, setOptions] = React.useState<IOption[]>([])
  const [hasMore, setHasMore] = React.useState(false)
  const [isLoadingMore, setIsLoadingMore] = React.useState(false)

  // Not state: read inside onMenuScrollToBottom without making it (and the ReduxSelect it's passed
  // to) re-render on every keystroke of a search.
  const pageRef = React.useRef(1)
  const searchRef = React.useRef<string | undefined>(undefined)

  const [fetchRecords, { loading, called }] = useLazyQuery<{
    injuryMasterRecords: { records: { url: string; name: string }[]; hasMore: boolean }
  }>(INJURY_MASTER_RECORDS_QUERY, {
    fetchPolicy: 'network-only'
  })

  const load = React.useCallback(
    async (search: string | undefined, page: number) => {
      const { data } = await fetchRecords({ variables: { listType, search, page } })

      const records: IOption[] = (data?.injuryMasterRecords?.records ?? []).map(({ url, name }) => ({
        value: url,
        label: name
      }))

      setOptions((prev) => (page === 1 ? records : [...prev, ...records]))
      setHasMore(data?.injuryMasterRecords?.hasMore ?? false)
      pageRef.current = page
      searchRef.current = search
    },
    [fetchRecords, listType]
  )

  const debouncedSearch = React.useMemo(() => debounce((search: string) => load(search, 1), 400), [load])

  const onMenuOpen = () => {
    if (!called) {
      load(undefined, 1)
    }
  }

  const onMenuScrollToBottom = async () => {
    if (!hasMore || loading || isLoadingMore) {
      return
    }

    setIsLoadingMore(true)
    await load(searchRef.current, pageRef.current + 1)
    setIsLoadingMore(false)
  }

  return (
    <>
      <ReduxSelect
        isMultiple={isMultiple}
        isClearable={!isRequired}
        validate={isRequired ? required : undefined}
        menuPortalTarget={null}
        label={label}
        name={name}
        options={options}
        isLoading={loading}
        onMenuOpen={onMenuOpen}
        onMenuScrollToBottom={onMenuScrollToBottom}
        handleSearch={debouncedSearch}
      />
      {isLoadingMore && <Hint>{LOADING_MORE_HINT}</Hint>}
    </>
  )
}

export default RecordPicker
